import { PortableText, toPlainText } from '@portabletext/react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import slugify from 'slugify';

import { sanityDraftsClient, changelogEntryFilter } from '@dashboard/lib/sanity/client';
import { formatDate } from '@dashboard/lib/format-date';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import ButtonComponent from '@dashboard/lib/sanity/portableText/components/ButtonComponent';
import ImageComponent from '@dashboard/lib/sanity/portableText/components/ImageComponent';
import InternalLinkComponent from '@dashboard/lib/sanity/portableText/components/InternalLinkComponent';
import LinkComponent from '@dashboard/lib/sanity/portableText/components/LinkComponent';
import YouTubeComponent from '@dashboard/lib/sanity/portableText/components/YouTubeComponent';
import TableComponent from '@dashboard/lib/sanity/portableText/components/TableComponent';

function UpdateContent({ content }) {
  return (
    <PortableText
      value={content}
      components={{
        types: {
          divider: () => <hr />,
          button: ButtonComponent,
          image: ImageComponent,
          code: ({ value }) => (
            <pre>
              <code>{value.code}</code>
            </pre>
          ),
          youtube: YouTubeComponent,
          table: TableComponent,
        },
        marks: {
          link: LinkComponent,
          internalLink: InternalLinkComponent,
          decorators: {
            code: ({ children }) => <code>{children}</code>,
          },
        },
        block: {
          h2: ({ value }) => {
            const hasLink = value.markDefs.some(def => def._type === 'link');
            const linkKey = hasLink ? value.markDefs.find(def => def._type === 'link')._key : null;
            const content = value.children.map((child, index) => {
              if (child.marks.includes(linkKey)) {
                return (
                  <a
                    key={index}
                    href={value.markDefs.find(def => def._key === linkKey).href}
                    className="font-bold"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {child.text}
                  </a>
                );
              }
              return child.text;
            });

            return <h2 id={slugify(toPlainText(value))}>{content}</h2>;
          },

          h3: ({ value }) => {
            const hasLink = value.markDefs.some(def => def._type === 'link');
            const linkKey = hasLink ? value.markDefs.find(def => def._type === 'link')._key : null;

            const content = value.children.map((child, index) => {
              if (child.marks.includes(linkKey)) {
                return (
                  <a
                    key={index}
                    href={value.markDefs.find(def => def._key === linkKey).href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="font-semibold"
                  >
                    {child.text}
                  </a>
                );
              }
              return child.text;
            });

            return <h3 id={slugify(toPlainText(value))}>{content}</h3>;
          },
        },
      }}
    />
  );
}

function UpdateSkeleton() {
  return (
    <article className="md:flex">
      <div className="pl-7 md:w-1/4 md:pl-0 md:pr-12 md:text-right">
        <Skeleton className="h-4 w-24 md:ml-auto" />
      </div>
      <div className="relative pt-2 pl-7 md:w-3/4 md:pt-0 md:pl-12 pb-16">
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6 mb-2" />
        <Skeleton className="h-4 w-4/6" />
        <div className="feed-border absolute -bottom-2 left-0 w-px bg-gray-200 dark:bg-accent -top-3 md:top-2.5"></div>
        <div className="feed-dot absolute -top-[1.0625rem] -left-1 h-[0.5625rem] w-[0.5625rem] rounded-full border-2 border-gray-300 dark:border-border bg-white dark:bg-background md:top-[0.4375rem]"></div>
      </div>
    </article>
  );
}

export default function Updates() {
  const location = useLocation();
  const [updates, setUpdates] = useState(null);

  // add scroll-behavior: smooth; to html
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';

    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  useEffect(() => {
    const fetchUpdates = async () => {
      const updates = await sanityDraftsClient.fetch(
        `
        *[${changelogEntryFilter}] | order(date desc){
          _id,
          title,
          date,
          body[] {
            ...,
            _type == "image" => {
              ...,
              asset->{
                ...
              }
            },
            markDefs[]{
              ...,
              _type == "internalLink" => {
                "slug": @.reference->slug,
                "type": @.reference->_type
              }
            },
            _type == 'button' => {
              ...,
              link {
                ...,
                reference->{
                  "slug": slug.current,
                  "type": _type
                }
              }
            }
          },
        }
        `,
      );

      setUpdates(updates);
    };

    fetchUpdates();
  }, []);

  // after updates are fetched, check if need to scroll to an update based on url hash
  useEffect(() => {
    const hash = location.hash.toString();

    if (hash) {
      let element;
      if (/^#[0-9]/.test(hash)) {
        element = document.getElementById(hash.substring(1));
      } else {
        element = document.querySelector(hash);
      }
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [updates]);

  return (
    <div className="my-16">
      <div className="flex flex-col items-center mb-16 max-w-xl mx-auto text-center">
        <h1 className="text-2xl mb-4">What's new</h1>
        <h3 className="text-xl font-normal">
          Stay up to date with the latest product improvements
        </h3>
      </div>

      <div className="max-w-3xl mx-auto">
        {updates === null && Array.from({ length: 3 }).map((_, i) => <UpdateSkeleton key={i} />)}
        {(updates ?? []).map(update => (
          <article
            key={update._id}
            className="md:flex"
            style={{
              'scroll-margin-top': '32px',
            }}
            id={update.date.slice(0, 10)}
          >
            <span id={update._id} style={{ scrollMarginTop: '32px' }} />
            <h2 className="pl-7 text-xs sm:text-sm text-gray-500 dark:text-muted-foreground font-normal md:w-1/4 md:pl-0 md:pr-12 md:text-right h-full mt-px">
              <a href={`#${update.date.slice(0, 10)}`}>
                {formatDate(update.date, { timeZone: 'UTC' })}
              </a>
            </h2>

            <div className="relative pt-2 pl-7 md:w-3/4 md:pt-0 md:pl-12 pb-16">
              <h1 className="text-xl font-bold mb-4">{update.title}</h1>

              <div className="prose dark:prose-invert min-w-0 [overflow-wrap:anywhere]">
                <UpdateContent content={update.body} />
              </div>

              <div className="feed-border absolute -bottom-2 left-0 w-px bg-gray-200 dark:bg-accent -top-3 md:top-2.5"></div>
              <div className="feed-dot absolute -top-[1.0625rem] -left-1 h-[0.5625rem] w-[0.5625rem] rounded-full border-2 border-gray-300 dark:border-border bg-white dark:bg-background md:top-[0.4375rem]"></div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
